import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, ArrowUpRight, Target, Zap, ExternalLink, Loader2, Rocket, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { insightsApi, API } from '../../lib/api';
import { trackBoostArticle } from '../../lib/analytics';

const LIME = '#C7FA3E';
const DIM = 'rgba(199, 250, 62, 0.35)';

const numberFmt = (n) => new Intl.NumberFormat('en-US').format(Math.round(n || 0));

const DeltaBadge = ({ delta }) => {
  if (delta === null || delta === undefined) {
    return <span className="text-zinc-600 text-xs">—</span>;
  }
  if (delta === 0) {
    return <span className="text-zinc-500 text-xs">·</span>;
  }
  const up = delta > 0;
  return (
    <span
      className={`text-xs font-semibold ${up ? 'text-lime-400' : 'text-red-400'}`}
      title={up ? `Gained ${delta} positions` : `Dropped ${-delta} positions`}
    >
      {up ? '▲' : '▼'} {Math.abs(delta)}
    </span>
  );
};

const OpportunityBar = ({ score, maxScore }) => {
  const pct = maxScore > 0 ? Math.min(100, (score / maxScore) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 bg-zinc-900 overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: LIME }}
        />
      </div>
      <span className="text-zinc-500 text-xs tabular-nums">{numberFmt(score)}</span>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, subtext }) => (
  <div className="border border-zinc-800 bg-black/40 p-5" data-testid="striking-summary-card">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4" style={{ color: LIME }} />
      <span className="text-zinc-500 text-xs font-heading tracking-widest uppercase">{label}</span>
    </div>
    <div className="text-white text-3xl md:text-4xl font-heading leading-none tabular-nums">
      {value}
    </div>
    {subtext ? <div className="text-zinc-500 text-xs mt-2">{subtext}</div> : null}
  </div>
);

export const StrikingDistanceWidget = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posFilter, setPosFilter] = useState('all'); // 'all' | 'top10' | 'top20'
  const [limit, setLimit] = useState(20);
  // Per-row boost state: { [`${slug}::${keyword}`]: 'idle' | 'boosting' | 'boosted' | 'error' }
  const [rowBoostState, setRowBoostState] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Detect admin session on client (widget lives on public page).
    try {
      setIsAdmin(!!sessionStorage.getItem('admin_token'));
    } catch (e) {
      setIsAdmin(false);
    }
  }, []);

  const boostRow = async (r) => {
    const key = `${r.slug}::${r.keyword}`;
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      toast.error('Sign in as admin to run the boost action.');
      return;
    }
    setRowBoostState((s) => ({ ...s, [key]: 'boosting' }));
    try {
      const res = await fetch(`${API}/admin/insights/refresh-article`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug: r.slug, keyword: r.keyword }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data?.detail === 'string' ? data.detail : 'Refresh failed';
        toast.error(msg);
        setRowBoostState((s) => ({ ...s, [key]: 'error' }));
        return;
      }
      const linksSummary = data.linked_from?.length
        ? ` · +${data.linked_from.length} internal ${data.linked_from.length === 1 ? 'link' : 'links'}`
        : '';
      const indexNowOk = data.indexnow?.ok ? ` · IndexNow ✓` : '';
      toast.success(`Boosted "${r.keyword}"${linksSummary}${indexNowOk}`);
      trackBoostArticle(r.slug, r.keyword);
      setRowBoostState((s) => ({ ...s, [key]: 'boosted' }));
    } catch (e) {
      toast.error(e?.message || 'Refresh failed');
      setRowBoostState((s) => ({ ...s, [key]: 'error' }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const minPos = 4;
    const maxPos = posFilter === 'top10' ? 10 : 20;
    Promise.all([
      insightsApi.strikingDistanceSummary(),
      insightsApi.strikingDistance({ min_pos: minPos, max_pos: maxPos, limit, min_volume: 10 }),
    ])
      .then(([s, r]) => {
        if (cancelled) return;
        setSummary(s.data);
        setRows(r.data.rows || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load insights');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [posFilter, limit]);

  const maxScore = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.opportunity_score || 0), 0),
    [rows]
  );

  return (
    <section
      className="mt-4 border border-zinc-800 bg-zinc-950/40"
      data-testid="striking-distance-widget"
    >
      {/* Header + summary */}
      <div className="p-6 border-b border-zinc-800">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-widest uppercase font-heading"
                style={{ backgroundColor: LIME, color: '#000' }}
              >
                <Target className="w-3 h-3" /> SEMRush data
              </span>
              <span className="text-zinc-600 text-xs">Refreshed every 15 min</span>
            </div>
            <h2 className="text-2xl md:text-3xl text-white font-heading leading-tight">
              STRIKING <span style={{ color: LIME }}>DISTANCE</span> KEYWORDS
            </h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
              Pages already ranked in positions 4-20 for real search demand. One internal-link
              boost, refreshed date, or improved meta can push them into the top 3.
            </p>
          </div>
          <div className="flex gap-2" data-testid="striking-position-filter">
            {[
              { id: 'top10', label: 'Pos 4-10' },
              { id: 'top20', label: 'Pos 4-20' },
              { id: 'all', label: 'All' },
            ].map((btn) => (
              <button
                key={btn.id}
                type="button"
                onClick={() => setPosFilter(btn.id === 'all' ? 'top20' : btn.id)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-all ${
                  (posFilter === btn.id || (btn.id === 'all' && posFilter === 'top20'))
                    ? 'text-black'
                    : 'text-zinc-400 hover:text-zinc-200 border border-zinc-800'
                }`}
                style={{
                  backgroundColor:
                    posFilter === btn.id || (btn.id === 'all' && posFilter === 'top20')
                      ? LIME
                      : 'transparent',
                }}
                data-testid={`filter-${btn.id}`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {summary ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <SummaryCard
              icon={Zap}
              label="Opportunities"
              value={numberFmt(summary.total_opportunities)}
              subtext={`across ${numberFmt(summary.unique_articles)} articles`}
            />
            <SummaryCard
              icon={TrendingUp}
              label="Near top"
              value={numberFmt(summary.positions_4_to_10)}
              subtext="positions 4-10"
            />
            <SummaryCard
              icon={ArrowUpRight}
              label="Building"
              value={numberFmt(summary.positions_11_to_20)}
              subtext="positions 11-20"
            />
            <SummaryCard
              icon={Target}
              label="Monthly volume"
              value={numberFmt(summary.total_addressable_volume)}
              subtext="addressable searches"
            />
          </div>
        ) : null}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div
            className="flex items-center gap-3 text-zinc-400 py-16 justify-center"
            data-testid="striking-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading striking-distance opportunities…
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm p-6" data-testid="striking-error">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-zinc-500 text-sm p-6" data-testid="striking-empty">
            No opportunities match the current filter.
          </div>
        ) : (
          <table
            className="w-full text-sm"
            style={{ borderColor: DIM }}
            data-testid="striking-table"
          >
            <thead>
              <tr className="text-zinc-500 text-[11px] uppercase tracking-widest">
                <th className="text-left px-4 py-3 font-heading">Pos</th>
                <th className="text-left px-4 py-3 font-heading">Δ</th>
                <th className="text-left px-4 py-3 font-heading">Keyword</th>
                <th className="text-right px-4 py-3 font-heading">Volume</th>
                <th className="text-right px-4 py-3 font-heading">KD</th>
                <th className="text-left px-4 py-3 font-heading">Opportunity</th>
                <th className="text-left px-4 py-3 font-heading">Article</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.slug}-${r.keyword}-${i}`}
                  className="border-t border-zinc-900 hover:bg-zinc-900/40"
                  data-testid="striking-row"
                >
                  <td className="px-4 py-3 tabular-nums">
                    <span
                      className={`inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 text-xs font-semibold ${
                        r.position <= 10 ? 'text-black' : 'text-white bg-zinc-800'
                      }`}
                      style={{
                        backgroundColor: r.position <= 10 ? LIME : undefined,
                      }}
                    >
                      #{r.position}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <DeltaBadge delta={r.delta} />
                  </td>
                  <td className="px-4 py-3 text-white max-w-[220px] truncate" title={r.keyword}>
                    {r.keyword}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-300 tabular-nums">
                    {numberFmt(r.search_volume)}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-500 tabular-nums">
                    {r.keyword_difficulty}
                  </td>
                  <td className="px-4 py-3">
                    <OpportunityBar score={r.opportunity_score} maxScore={maxScore} />
                  </td>
                  <td
                    className="px-4 py-3 text-zinc-400 max-w-[260px] truncate"
                    title={r.article_title}
                  >
                    <span className="text-zinc-600 text-xs uppercase tracking-wider mr-2">
                      {r.category}
                    </span>
                    {r.article_title}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin ? (
                      (() => {
                        const key = `${r.slug}::${r.keyword}`;
                        const state = rowBoostState[key] || 'idle';
                        if (state === 'boosting') {
                          return (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-zinc-400"
                              data-testid="striking-row-boosting"
                            >
                              <Loader2 className="w-3 h-3 animate-spin" /> Boosting…
                            </span>
                          );
                        }
                        if (state === 'boosted') {
                          return (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-black px-2 py-1"
                              style={{ backgroundColor: LIME }}
                              data-testid="striking-row-boosted"
                            >
                              <Check className="w-3 h-3" strokeWidth={3} /> Boosted
                            </span>
                          );
                        }
                        if (state === 'error') {
                          return (
                            <button
                              type="button"
                              onClick={() => boostRow(r)}
                              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                              data-testid="striking-row-retry"
                            >
                              <AlertCircle className="w-3 h-3" /> Retry
                            </button>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => boostRow(r)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-black px-2.5 py-1 transition-all hover:-translate-y-0.5"
                            style={{ backgroundColor: LIME }}
                            data-testid="striking-row-boost-btn"
                            title="Bump publish date, add fresh internal links from related articles, and re-submit to IndexNow"
                          >
                            <Rocket className="w-3 h-3" /> Boost
                          </button>
                        );
                      })()
                    ) : (
                      <a
                        href={`/blogs/article/${r.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
                        data-testid="striking-row-link"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Load more */}
      {!loading && rows.length >= limit ? (
        <div className="border-t border-zinc-900 p-4 flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((v) => v + 20)}
            className="px-5 py-2 text-xs uppercase tracking-wider text-zinc-400 border border-zinc-800 hover:text-white hover:border-zinc-600 transition-all"
            data-testid="striking-load-more"
          >
            Load 20 more
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default StrikingDistanceWidget;
